export const FLOW_STATES = {
    pending: 'pending',
    processing: 'processing',
    completed: 'completed',
    warning: 'warning',
    failed: 'failed',
};

export const VERIFICATION_BRANCHES = [
    {
        id: 'document-forensics',
        label: 'Document Forensics',
        icon: 'ri-file-search-line',
        stages: [
            'Quality',
            'Consistency',
            'Forgery Likelihood',
        ],
    },
    {
        id: 'company-intelligence',
        label: 'Company Intelligence',
        icon: 'ri-building-line',
        stages: [
            'Existence',
            'Identity',
            'Ownership',
            'Status',
            'Data Consistency',
        ],
    },
    {
        id: 'cyber-security',
        label: 'Cyber Security Check',
        icon: 'ri-shield-check-line',
        stages: [
            'Domain & Ownership',
            'DNS & Infrastructure',
            'Email Authentication',
            'Website Security',
            'Reputation & Threat',
        ],
    },
    {
        id: 'human-identity',
        label: 'Human Identity',
        icon: 'ri-user-voice-line',
        stages: [
            'Identity Verification',
            'Identity Consistency',
            'Call verification',
        ],
    },
];

/**
 * Explicit data dependencies. Visual order and stage index never create a wait.
 * A stage waits only when `waitTarget` names that stage.
 */
export const CROSS_BRANCH_DEPENDENCIES = [
    {
        source: 'document-forensics-stage-1',
        sourceBranch: 'document-forensics',
        sourceStage: 'document-forensics-stage-1',
        target: 'company-intelligence',
        targetBranch: 'company-intelligence',
        targetNode: 'company-intelligence',
        sourceHandle: 'start',
        targetHandle: 'end',
        transferredData: ['documentQuality', 'extractedIdentity'],
    },
];

const LAYOUT = {
    bidX: 0,
    branchX: 330,
    branchStatusX: 570,
    stageX: 660,
    branchOutputX: 930,
    analysisX: 1110,
    decisionX: 1290,
    firstBranchY: 70,
    branchGap: 160,
    stageGap: 30,
};

const getStageId = (branchId, stageIndex) => `${branchId}-stage-${stageIndex + 1}`;

const createRelationship = ({
    branchId,
    dependencyType,
    level,
    source,
    sourceBranch,
    sourceHandle,
    sourceStage,
    target,
    targetBranch,
    targetHandle,
    targetNode,
    transferredData,
}) => ({
    id: `${source}--${target}`,
    source,
    sourceBranch,
    sourceHandle,
    sourceStage,
    target,
    targetBranch,
    targetHandle,
    targetNode,
    branchId,
    dependencyType,
    level,
    transferredData,
    state: FLOW_STATES.pending,
});

export const createFlowDefinition = (bid) => {
    const centerY = LAYOUT.firstBranchY
        + (((VERIFICATION_BRANCHES.length - 1) * LAYOUT.branchGap) / 2);
    const nodes = [
        {
            id: 'bid',
            role: 'bid',
            type: 'bidSource',
            label: bid.freightReference,
            detail: bid.carrier,
            position: { x: LAYOUT.bidX, y: centerY - 80 },
            state: FLOW_STATES.pending,
        },
    ];
    const relationships = [];

    VERIFICATION_BRANCHES.forEach((branch, branchIndex) => {
        const branchCenterY = LAYOUT.firstBranchY + (branchIndex * LAYOUT.branchGap);
        const branchStatusId = `${branch.id}-status`;
        const branchOutputId = `${branch.id}-output`;

        nodes.push(
            {
                id: branch.id,
                role: 'branch',
                type: 'branch',
                branchId: branch.id,
                label: branch.label,
                icon: branch.icon,
                totalStages: branch.stages.length,
                position: { x: LAYOUT.branchX, y: branchCenterY - 22 },
                state: FLOW_STATES.pending,
            },
            {
                id: branchStatusId,
                role: 'branchStatus',
                type: 'junction',
                branchId: branch.id,
                label: branch.label,
                position: { x: LAYOUT.branchStatusX, y: branchCenterY - 9 },
                state: FLOW_STATES.pending,
            },
            {
                id: branchOutputId,
                role: 'branchOutput',
                type: 'junction',
                branchId: branch.id,
                label: `${branch.label} result`,
                position: { x: LAYOUT.branchOutputX, y: branchCenterY - 9 },
                state: FLOW_STATES.pending,
            }
        );

        relationships.push(
            createRelationship({
                source: 'bid',
                target: branch.id,
                branchId: branch.id,
                dependencyType: 'starts',
                level: 'primary',
                transferredData: ['bidContext'],
            }),
            createRelationship({
                source: branch.id,
                target: branchStatusId,
                branchId: branch.id,
                dependencyType: 'branch-progress',
                level: 'primary',
                transferredData: ['branchProgress'],
            }),
            createRelationship({
                source: branchStatusId,
                target: branchOutputId,
                branchId: branch.id,
                dependencyType: 'branch-summary',
                level: 'primary',
                transferredData: ['branchSummary'],
            }),
            createRelationship({
                source: branchOutputId,
                target: 'central-analysis',
                branchId: branch.id,
                dependencyType: 'analysis-input',
                level: 'primary',
                transferredData: ['branchSummary', 'riskIndicators'],
            })
        );

        const stagesTopOffset = ((branch.stages.length - 1) * LAYOUT.stageGap) / 2;

        branch.stages.forEach((stageLabel, stageIndex) => {
            const stageId = getStageId(branch.id, stageIndex);

            nodes.push({
                id: stageId,
                role: 'stage',
                type: 'stage',
                branchId: branch.id,
                label: stageLabel,
                position: {
                    x: LAYOUT.stageX,
                    y: (branchCenterY - stagesTopOffset) + (stageIndex * LAYOUT.stageGap) - 10,
                },
                state: FLOW_STATES.pending,
            });

            relationships.push(
                createRelationship({
                    source: branchStatusId,
                    target: stageId,
                    branchId: branch.id,
                    dependencyType: 'check',
                    level: 'stage',
                    transferredData: ['branchContext'],
                }),
                createRelationship({
                    source: stageId,
                    target: branchOutputId,
                    branchId: branch.id,
                    dependencyType: 'check-result',
                    level: 'stage',
                    transferredData: ['checkResult'],
                })
            );
        });
    });

    nodes.push(
        {
            id: 'central-analysis',
            role: 'analysis',
            type: 'analysis',
            label: 'Central Analysis',
            totalBranches: VERIFICATION_BRANCHES.length,
            position: { x: LAYOUT.analysisX, y: centerY - 55 },
            state: FLOW_STATES.pending,
        },
        {
            id: 'final-decision',
            role: 'decision',
            type: 'decision',
            label: 'Final Decision',
            detail: bid.outcome.status,
            position: { x: LAYOUT.decisionX, y: centerY - 45 },
            state: FLOW_STATES.pending,
        }
    );

    relationships.push(createRelationship({
        source: 'central-analysis',
        target: 'final-decision',
        dependencyType: 'decision-input',
        level: 'primary',
        transferredData: ['analysisResult', 'recommendation'],
    }));

    CROSS_BRANCH_DEPENDENCIES.forEach((dependency) => {
        relationships.push(createRelationship({
            source: dependency.source,
            sourceBranch: dependency.sourceBranch,
            sourceHandle: dependency.sourceHandle,
            sourceStage: dependency.sourceStage,
            target: dependency.target,
            targetBranch: dependency.targetBranch,
            targetHandle: dependency.targetHandle,
            targetNode: dependency.targetNode,
            dependencyType: 'data-dependency',
            level: 'dependency',
            transferredData: dependency.transferredData,
        }));
    });

    return {
        nodes,
        relationships,
    };
};
